import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  IconArrowUpRight,
  IconChartBar,
  IconCheck,
  IconDatabase,
  IconDeviceDesktop,
  IconSparkles,
  IconUsers,
} from "@tabler/icons-react";
import { fadeUp, fadeLeft, fadeRight } from "./animations";

const products = [
  {
    id: 1,
    slug: "billing-software",

    category: "Business Software",

    name: "Billing Software",

    headline: "Billing made simpler.   Business made smarter.",

    description:
      "A complete billing and shop management solution designed to help businesses handle sales, inventory, customers and everyday operations from one simple system.",

    features: [
      "Business & Sales Dashboard",
      "Inventory Management Software",
      "Customer & CRM Management",
      "Sales & Business Reports",
      "Product Management",
      "Fast GST Billing & Invoicing",
      "WhatsApp Integration",
      'Multi-Store & Multi-User Support',
      "UPI & Online Payment Integration",
      "AI Business Assistant"
    ],

    stats: [
      {
        label: "Billing",
        value: "Faster",
      },
      {
        label: "Inventory",
        value: "Live",
      },
      {
        label: "Reports",
        value: "Smart",
      },
    ],

    route: "/products/billing-software",

    status: "Available",

    accent: "#55aef3",
    visualType: "billing",
  },

  /*
  ==========================================================
  FUTURE PRODUCT EXAMPLE

  Just uncomment and change the content.
  ==========================================================

  {
    id: 2,
    slug: "crm",

    category: "Customer Management",

    name: "WE3X CRM",

    headline: "Turn every lead into an opportunity.",

    description:
      "A CRM platform designed to help businesses manage leads,
      customers, follow-ups and sales pipelines.",

    features: [
      "Lead Management",
      "Customer Management",
      "Follow-ups",
      "Sales Pipeline",
      "Reports",
      "Team Management",
    ],

    stats: [
      {
        label: "Leads",
        value: "Organised",
      },
      {
        label: "Follow-ups",
        value: "Tracked",
      },
      {
        label: "Sales",
        value: "Visible",
      },
    ],

    route: "/products/crm",

    status: "Coming Soon",

    accent: "#55aef3",
  },

  */
];

export default function TechnologyProducts() {
  return <ProductShowcase products={products} />;
}

function ProductShowcase({ products }) {
  return (
    <section
      className="
        relative
        overflow-hidden
        bg-white
        py-20
        sm:py-24
        lg:py-32
      "
    >
      <div
        className="
          pointer-events-none
          absolute
          right-[-180px]
          top-[100px]
          h-[500px]
          w-[500px]
          rounded-full
          bg-[#55aef3]/10
          blur-[140px]
        "
      />

      <div
        className="
          relative
          z-10
          mx-auto
          max-w-7xl
          px-5
          sm:px-8
          lg:px-10
        "
      >
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="
            grid
            gap-8
            lg:grid-cols-[1fr_0.65fr]
            lg:items-end
          "
        >
          <div>
            <SectionLabel>Products by WE3X</SectionLabel>

            <h2
              className="
                merriweather
                mt-5
                max-w-[760px]
                text-3xl
                font-black
                tracking-[-0.04em]
                text-neutral-950
                sm:text-4xl
                lg:text-[54px]
                lg:leading-[1.08]
              "
            >
              Technology we've built
              <span className="block text-[#55aef3]">
                to solve real business problems.
              </span>
            </h2>
          </div>

          <p
            className="
              max-w-xl
              text-[15px]
              leading-8
              text-neutral-600
            "
          >
            Alongside custom development, WE3X builds its own digital products
            designed to simplify everyday business operations and make
            technology easier to use.
          </p>
        </motion.div>

        <div className="mt-14 space-y-8 lg:mt-16">
          {products.map((product, index) => (
            <ProductFeature
              key={product.id}
              product={product}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductFeature({ product, index }) {
  const reverse = index % 2 !== 0;
  const available = product.status?.toLowerCase() === "available";

  return (
    <motion.article
      initial={{
        opacity: 0,
        y: 50,
        scale: 0.98,
      }}
      whileInView={{
        opacity: 1,
        y: 0,
        scale: 1,
      }}
      viewport={{
        once: true,
        amount: 0.12,
      }}
      transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="
        relative
        overflow-hidden
        rounded-[26px]
        border
        border-neutral-200
        bg-[#f6f6f6]
      "
    >
      <div
        className={`
          pointer-events-none
          absolute
          top-1/2
          h-[400px]
          w-[400px]
          -translate-y-1/2
          rounded-full
          bg-[#55aef3]/10
          blur-[120px]
          ${reverse ? "-left-[180px]" : "-right-[180px]"}
        `}
      />

      <div
        className="
          relative
          z-10
          grid
          items-center
          gap-10
          p-5
          sm:p-8
          lg:grid-cols-2
          lg:gap-14
          lg:p-10
        "
      >
        <motion.div
          variants={reverse ? fadeRight : fadeLeft}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className={reverse ? "lg:order-2" : ""}
        >
          <div className="flex flex-wrap items-center gap-3">
            <span
              className="
                inline-flex
                items-center
                gap-2
                rounded-full
                border
                border-blue-100
                bg-blue-50
                px-3
                py-1.5
                text-[10px]
                font-bold
                uppercase
                tracking-[0.16em]
                text-[#3899e4]
              "
            >
              {product.category}
            </span>

            <span
              className="
                inline-flex
                items-center
                gap-2
                text-[10px]
                font-bold
                uppercase
                tracking-[0.14em]
                text-neutral-400
              "
            >
              <span
                className={`
                  h-1.5
                  w-1.5
                  rounded-full
                  ${available ? "bg-green-500" : "bg-amber-500"}
                `}
              />
              {product.status}
            </span>
          </div>

          <p
            className="
              mt-7
              text-[11px]
              font-bold
              uppercase
              tracking-[0.2em]
              text-neutral-400
            "
          >
            {product.name}
          </p>

          <h3
            className="
              merriweather
              mt-3
              max-w-xl
              text-3xl
              font-black
              leading-[1.08]
              tracking-[-0.04em]
              text-neutral-950
              sm:text-4xl
              lg:text-[46px]
            "
          >
            {product.headline}
          </h3>

          <p
            className="
              mt-6
              max-w-xl
              text-[15px]
              leading-8
              text-neutral-600
            "
          >
            {product.description}
          </p>

          <div
            className="
              mt-7
              grid
              gap-x-5
              gap-y-3
              sm:grid-cols-2
            "
          >
            {product.features.map((feature) => (
              <div
                key={feature}
                className="
                  flex
                  items-center
                  gap-3
                  text-sm
                  font-semibold
                  text-neutral-700
                "
              >
                <div
                  className="
                    flex
                    h-5
                    w-5
                    shrink-0
                    items-center
                    justify-center
                    rounded-full
                    bg-blue-50
                    text-[#55aef3]
                  "
                >
                  <IconCheck size={12} stroke={2.5} />
                </div>
                {feature}
              </div>
            ))}
          </div>

          <Link
            to={product.route}
            className="
              group
              mt-9
              inline-flex
              items-center
              gap-2
              rounded-[9px]
              bg-[#151515]
              px-6
              py-3.5
              text-sm
              font-bold
              text-white
              transition-all
              duration-300
              hover:-translate-y-1
              hover:bg-[#55aef3]
            "
          >
            Explore {product.name}

            <IconArrowUpRight
              size={17}
              className="
                transition-transform
                duration-300
                group-hover:translate-x-1
                group-hover:-translate-y-1
              "
            />
          </Link>
        </motion.div>

        <div className={reverse ? "lg:order-1" : ""}>
          <ProductVisual product={product} />
        </div>
      </div>
    </motion.article>
  );
}

function ProductVisual({ product }) {
  switch (product.visualType) {
    case "billing":
      return <BillingSoftwareVisual product={product} />;

    default:
      return <GenericProductVisual product={product} />;
  }
}

function BillingSoftwareVisual({ product }) {
  return (
    <motion.div
      initial={{
        opacity: 0,
        x: 40,
        rotateY: -5,
      }}
      whileInView={{
        opacity: 1,
        x: 0,
        rotateY: 0,
      }}
      viewport={{ once: true }}
      transition={{
        duration: 0.9,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="
        relative
        mx-auto
        w-full
        max-w-[620px]
      "
      style={{
        perspective: "1000px",
      }}
    >
      <div
        className="
          absolute
          -bottom-4
          left-[8%]
          h-[90%]
          w-[92%]
          rounded-[24px]
          bg-[#55aef3]/10
        "
      />

      <motion.div
        whileHover={{
          y: -5,
          rotateX: 1,
          rotateY: -1,
        }}
        transition={{
          duration: 0.3,
        }}
        className="
          relative
          z-10
          overflow-hidden
          rounded-[22px]
          border
          border-neutral-200
          bg-[#101010]
          shadow-2xl
          shadow-black/[0.12]
        "
      >
        <div
          className="
            flex
            items-center
            justify-between
            border-b
            border-white/10
            px-5
            py-4
          "
        >
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          </div>

          <p
            className="
              text-[9px]
              font-bold
              uppercase
              tracking-[0.16em]
              text-white/30
            "
          >
            {product.name}
          </p>
        </div>

        <div
          className="
            grid
            min-h-[420px]
            sm:grid-cols-[150px_1fr]
          "
        >
          <div
            className="
              hidden
              border-r
              border-white/10
              bg-[#0b0b0b]
              p-4
              sm:block
            "
          >
            <div
              className="
                flex
                h-9
                w-9
                items-center
                justify-center
                rounded-[10px]
                bg-[#55aef3]
                text-white
              "
            >
              <IconDeviceDesktop size={18} />
            </div>

            <div className="mt-7 space-y-2">
              <SoftwareMenu text="Dashboard" active />
              <SoftwareMenu text="Billing" />
              <SoftwareMenu text="Products" />
              <SoftwareMenu text="Customers" />
              <SoftwareMenu text="Reports" />
            </div>
          </div>

          <div className="bg-[#f6f6f6] p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-medium text-neutral-400">
                  Business Overview
                </p>

                <p className="mt-1 text-sm font-bold text-neutral-950">
                  Today's Dashboard
                </p>
              </div>

              <div
                className="
                  rounded-lg
                  bg-[#151515]
                  px-3
                  py-2
                  text-[9px]
                  font-bold
                  text-white
                "
              >
                + New Bill
              </div>
            </div>

            <div className="mt-5 grid gap-2 sm:grid-cols-3">
              {product.stats.map((stat) => (
                <ProductMetric
                  key={stat.label}
                  label={stat.label}
                  value={stat.value}
                />
              ))}
            </div>

            <div className="mt-3 rounded-[14px] bg-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-neutral-400">
                    Sales Performance
                  </p>

                  <p className="mt-1 text-sm font-bold">
                    This Week
                  </p>
                </div>

                <IconChartBar
                  size={20}
                  className="text-[#55aef3]"
                />
              </div>

              <div
                className="
                  mt-6
                  flex
                  h-[110px]
                  items-end
                  gap-2
                "
              >
                {[38, 54, 44, 70, 58, 84, 72, 94].map((height, index) => (
                  <motion.div
                    key={index}
                    initial={{ height: 0 }}
                    whileInView={{
                      height: `${height}%`,
                    }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.7,
                      delay: index * 0.06,
                    }}
                    className="
                      flex-1
                      rounded-t-[4px]
                      bg-[#55aef3]
                    "
                  />
                ))}
              </div>
            </div>

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <ProductDataBox
                icon={<IconDatabase size={18} />}
                title="Inventory"
                text="Stock updated"
              />

              <ProductDataBox
                icon={<IconUsers size={18} />}
                title="Customers"
                text="Connected"
              />
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        animate={{
          y: [0, -7, 0],
        }}
        transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="
          absolute
          -bottom-5
          -left-3
          z-20
          hidden
          rounded-[13px]
          border
          border-neutral-200
          bg-white
          px-4
          py-3
          shadow-xl
          shadow-black/[0.06]
          md:block
        "
      >
        <div className="flex items-center gap-3">
          <span className="relative flex h-2 w-2">
            <span
              className="
                absolute
                inline-flex
                h-full
                w-full
                animate-ping
                rounded-full
                bg-green-400
                opacity-75
              "
            />

            <span
              className="
                relative
                inline-flex
                h-2
                w-2
                rounded-full
                bg-green-500
              "
            />
          </span>

          <div>
            <p
              className="
                text-[9px]
                uppercase
                tracking-[0.15em]
                text-neutral-400
              "
            >
              System
            </p>

            <p className="mt-0.5 text-xs font-bold">
              Ready for business
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function GenericProductVisual({ product }) {
  return (
    <motion.div
      initial={{
        opacity: 0,
        scale: 0.96,
      }}
      whileInView={{
        opacity: 1,
        scale: 1,
      }}
      viewport={{ once: true }}
      transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="
        relative
        overflow-hidden
        rounded-[22px]
        border
        border-neutral-200
        bg-[#101010]
        p-5
        text-white
        shadow-2xl
        shadow-black/[0.1]
      "
    >
      <div
        className="
          pointer-events-none
          absolute
          -right-24
          -top-24
          h-72
          w-72
          rounded-full
          bg-[#55aef3]/15
          blur-[90px]
        "
      />

      <div className="relative z-10">
        <div
          className="
            flex
            items-center
            justify-between
            border-b
            border-white/10
            pb-4
          "
        >
          <div className="flex gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
          </div>

          <span className="text-[10px] text-white/30">
            {product.name}
          </span>
        </div>

        <div
          className="
            flex
            min-h-[380px]
            flex-col
            items-center
            justify-center
            text-center
          "
        >
          <div
            className="
              flex
              h-16
              w-16
              items-center
              justify-center
              rounded-2xl
              bg-[#55aef3]
            "
          >
            <IconSparkles size={28} />
          </div>

          <p
            className="
              merriweather
              mt-6
              max-w-sm
              text-2xl
              font-black
            "
          >
            {product.name}
          </p>

          <p
            className="
              mt-3
              max-w-sm
              text-sm
              leading-7
              text-white/45
            "
          >
            Product preview ready for your next WE3X product.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

function SoftwareMenu({ text, active = false }) {
  return (
    <div
      className={`
        rounded-lg
        px-3
        py-2.5
        text-[10px]
        font-semibold
        transition
        ${
          active
            ? "bg-[#55aef3] text-white"
            : "text-white/40 hover:bg-white/[0.05] hover:text-white"
        }
      `}
    >
      {text}
    </div>
  );
}

function ProductMetric({ label, value }) {
  return (
    <div className="rounded-[12px] bg-white p-3">
      <p className="text-[9px] text-neutral-400">
        {label}
      </p>

      <p className="mt-2 text-sm font-black text-neutral-950">
        {value}
      </p>
    </div>
  );
}

function ProductDataBox({ icon, title, text }) {
  return (
    <div
      className="
        flex
        items-center
        gap-3
        rounded-[12px]
        bg-white
        p-3
      "
    >
      <div
        className="
          flex
          h-9
          w-9
          shrink-0
          items-center
          justify-center
          rounded-[9px]
          bg-blue-50
          text-[#55aef3]
        "
      >
        {icon}
      </div>

      <div>
        <p className="text-[10px] font-bold text-neutral-900">
          {title}
        </p>

        <p className="mt-0.5 text-[9px] text-neutral-400">
          {text}
        </p>
      </div>
    </div>
  );
}

function SectionLabel({ children, dark = false }) {
  return (
    <div
      className={`
        flex
        items-center
        gap-3
        text-[10px]
        font-bold
        uppercase
        tracking-[0.2em]
        ${dark ? "text-[#55aef3]" : "text-[#3899e4]"}
      `}
    >
      <span className="h-[1px] w-8 bg-[#55aef3]" />

      {children}
    </div>
  );
}
